import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Plus, Sparkles, History,
} from "lucide-react";
import { toast } from "sonner";
import AiDraftDialog from "@/components/AiDraftDialog";
import UpsellStrip from "@/components/UpsellStrip";
import PropertyHistoryDialog from "@/components/PropertyHistoryDialog";

import { genLineId } from "./helpers";
import PropertyCard from "./PropertyCard";
import MeasurementsPanel from "./MeasurementsPanel";
import ServicesCatalog from "./ServicesCatalog";
import LineRow from "./LineRow";
import SummaryCard from "./SummaryCard";
import NewPropertyDialog from "./NewPropertyDialog";
import NewMeasurementDialog from "./NewMeasurementDialog";
import LineMeasureDialog from "./LineMeasureDialog";

// Serialize a UI line into the calculator/estimator payload shape.
const serializeLine = (l) => {
  const num = (v) => (v !== "" && v !== undefined && v !== null ? Number(v) : null);
  return {
    service_id: l.service_id || null,
    service_name: l.service_name || null,
    calculator_key: l.calculator_key || null,
    measurement_id: l.measurement_id || null,
    quantity: l.quantity !== "" ? Number(l.quantity) : null,
    pricing_method: l.pricing_method || null,
    unit_price: num(l.unit_price),
    min_charge: num(l.min_charge),
    material_cost_per_unit: num(l.material_cost_per_unit),
    material_markup_pct: num(l.material_markup_pct),
    labor_hours: num(l.labor_hours),
    labor_rate_per_hour: num(l.labor_rate_per_hour),
    price_override: num(l.price_override),
  };
};

export default function SmartEstimator() {
  const { id: customerId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [customer, setCustomer] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [calculators, setCalculators] = useState([]);
  const [settings, setSettings] = useState({});
  const [properties, setProperties] = useState([]);
  const [selectedProp, setSelectedProp] = useState(params.get("property_id") || null);
  const [measurements, setMeasurements] = useState([]);
  const [lines, setLines] = useState([]);
  const [priced, setPriced] = useState({ lines: [], summary: null, service_call_fee: 0 });
  const [saving, setSaving] = useState(false);
  const [showNewProp, setShowNewProp] = useState(false);
  const [showNewMeas, setShowNewMeas] = useState(false);
  const [measureLineIdx, setMeasureLineIdx] = useState(null);
  const [showAi, setShowAi] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [upsells, setUpsells] = useState([]);
  const [dismissedUpsells, setDismissedUpsells] = useState(new Set());
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");

  // ---- Bootstrapping ----
  useEffect(() => {
    (async () => {
      try {
        const [cust, srvs, cats, calcRes, setRes, propsRes] = await Promise.all([
          api.get(`/customers/${customerId}`),
          api.get("/services"),
          api.get("/service-categories"),
          api.get("/estimator/calculators"),
          api.get("/estimator/settings"),
          api.get(`/estimator/properties?customer_id=${customerId}`),
        ]);
        setCustomer(cust.data);
        setServices(srvs.data);
        setServiceCategories(cats.data || []);
        setCalculators(calcRes.data.calculators || []);
        setSettings(setRes.data || {});
        setProperties(propsRes.data);
        if (!selectedProp && propsRes.data.length > 0) setSelectedProp(propsRes.data[0].id);
        setTitle(`Estimate for ${cust.data.name}`);
      } catch (e) {
        toast.error("Failed to load estimator");
        nav(`/app/customers/${customerId}`);
      }
    })();
  }, [customerId]); // eslint-disable-line

  useEffect(() => {
    if (!selectedProp) { setMeasurements([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/estimator/properties/${selectedProp}/measurements`);
        setMeasurements(data);
      } catch { toast.error("Couldn't load measurements"); }
    })();
  }, [selectedProp]);

  // ---- Live pricing (debounced) ----
  useEffect(() => {
    const t = setTimeout(async () => {
      if (lines.length === 0) { setPriced({ lines: [], summary: null, service_call_fee: 0 }); return; }
      try {
        const { data } = await api.post("/estimator/calculate", {
          property_id: selectedProp,
          lines: lines.map(serializeLine),
        });
        setPriced(data);
      } catch { /* keep old */ }
    }, 250);
    return () => clearTimeout(t);
  }, [lines, selectedProp]);

  // ---- Upsell suggestions ----
  useEffect(() => {
    const selectedIds = lines.map((l) => l.service_id).filter(Boolean);
    api.post("/estimator/upsells/suggest", {
      customer_id: customerId,
      property_id: selectedProp,
      selected_service_ids: selectedIds,
      limit: 6,
    }).then((r) => setUpsells(r.data.suggestions || []))
      .catch(() => setUpsells([]));
  }, [lines, selectedProp, customerId]);

  const marginTarget = Number(settings.default_margin_target_pct || 0);

  const addLine = (svc = null, measurement = null) => {
    const line = {
      _id: genLineId(),
      service_id: svc?.id || null,
      service_name: svc?.name || (measurement ? measurement.label : "Custom line"),
      calculator_key: svc?.estimator_config?.calculator_key || measurement?.calculator_key || "custom",
      measurement_id: measurement?.id || null,
      quantity: measurement?.output_value || 0,
      pricing_method: svc?.estimator_config?.pricing_method || "unit",
      unit_price: svc?.estimator_config?.unit_price ?? svc?.default_price ?? "",
      min_charge: svc?.estimator_config?.min_charge ?? "",
      material_cost_per_unit: svc?.estimator_config?.material_cost_per_unit ?? "",
      material_markup_pct: svc?.estimator_config?.material_markup_pct ?? "",
      labor_hours: "",
      labor_rate_per_hour: svc?.estimator_config?.labor_rate_per_hour ?? "",
      price_override: "",
    };
    setLines((ls) => [...ls, line]);
  };

  const updateLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const addAiLines = (aiLines) => {
    const newLines = aiLines.map((al) => {
      const svc = al.service_id ? services.find((s) => s.id === al.service_id) : null;
      const meas = al.measurement_id ? measurements.find((m) => m.id === al.measurement_id) : null;
      const cfg = svc?.estimator_config || {};
      return {
        _id: genLineId(),
        service_id: svc?.id || null,
        service_name: svc?.name || al.service_name || "AI line",
        calculator_key: al.calculator_key || cfg.calculator_key || "custom",
        measurement_id: meas?.id || null,
        quantity: al.quantity ?? meas?.output_value ?? 0,
        pricing_method: cfg.pricing_method || "unit",
        unit_price: cfg.unit_price ?? svc?.default_price ?? "",
        min_charge: cfg.min_charge ?? "",
        material_cost_per_unit: cfg.material_cost_per_unit ?? "",
        material_markup_pct: cfg.material_markup_pct ?? "",
        labor_hours: "",
        labor_rate_per_hour: cfg.labor_rate_per_hour ?? "",
        price_override: "",
        _ai_assumption: al.assumption || "",
      };
    });
    setLines((ls) => [...ls, ...newLines]);
    toast.success(`Added ${newLines.length} AI-suggested line${newLines.length > 1 ? "s" : ""}`);
  };

  const addUpsellLine = (s) => {
    const svc = services.find((x) => x.id === s.service_id);
    if (svc) addLine(svc);
    else {
      setLines((ls) => [...ls, {
        _id: genLineId(),
        service_id: s.service_id,
        service_name: s.service_name,
        calculator_key: s.calculator_key || "custom",
        measurement_id: null,
        quantity: 1,
        pricing_method: s.pricing_method || "unit",
        unit_price: s.unit_price ?? s.default_price ?? "",
        min_charge: "", material_cost_per_unit: "", material_markup_pct: "",
        labor_hours: "", labor_rate_per_hour: "", price_override: "",
      }]);
    }
    setDismissedUpsells((d) => new Set([...d, s.service_id]));
  };

  const generate = async () => {
    if (!customerId) return;
    if (lines.length === 0) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/estimator/estimates", {
        customer_id: customerId,
        property_id: selectedProp,
        title, notes,
        lines: lines.map(serializeLine),
      });
      toast.success("Estimate created");
      nav(`/app/estimates/${data.estimate.id}`);
    } catch { toast.error("Failed to create estimate"); }
    finally { setSaving(false); }
  };

  const summary = priced.summary || { customer_price: 0, internal_cost: 0, profit: 0, margin_pct: 0, service_call_fee: 0 };
  const belowTargetCount = useMemo(
    () => (priced.lines || []).filter((l) => marginTarget > 0 && l.customer_price > 0 && l.margin_pct < marginTarget).length,
    [priced, marginTarget],
  );

  if (!customer) return <div className="p-8 text-zinc-500">Loading estimator…</div>;

  return (
    <div className="p-6 max-w-[1400px]" data-testid="smart-estimator-page">
      <button
        onClick={() => nav(`/app/customers/${customerId}`)}
        className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-3"
        data-testid="estimator-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back to {customer.name}
      </button>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Smart Estimator</div>
          <h1 className="font-heading text-3xl font-bold">Build estimate — {customer.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowAi(true)} variant="outline" className="border-[#FF5A00] text-[#FF5A00] hover:bg-orange-50" data-testid="estimator-ai-btn">
            <Sparkles className="w-4 h-4 mr-1"/> AI Draft
          </Button>
          <Button onClick={() => setShowHistory(true)} disabled={!selectedProp} variant="outline" data-testid="estimator-history-btn">
            <History className="w-4 h-4 mr-1"/> Property history
          </Button>
          <Button
            onClick={generate}
            disabled={saving || lines.length === 0}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
            data-testid="estimator-generate-btn"
          >
            <Sparkles className="w-4 h-4 mr-1" /> {saving ? "Generating…" : "Generate estimate"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 space-y-4">
          <PropertyCard
            properties={properties}
            selectedId={selectedProp}
            onSelect={setSelectedProp}
            onNew={() => setShowNewProp(true)}
          />
          <MeasurementsPanel
            selectedProp={selectedProp}
            measurements={measurements}
            setMeasurements={setMeasurements}
            onAddLine={(m) => addLine(null, m)}
            onNewMeasurement={() => setShowNewMeas(true)}
            propertyAddress={properties.find(p => p.id === selectedProp)?.address}
            propertyLat={properties.find(p => p.id === selectedProp)?.lat}
            propertyLng={properties.find(p => p.id === selectedProp)?.lng}
          />
          <ServicesCatalog services={services} categories={serviceCategories} onPick={addLine} />
        </div>

        <div className="lg:col-span-8 space-y-4">
          <div className="bg-white border border-zinc-200 rounded-xl p-4 card-crisp">
            <div className="flex items-center gap-2 mb-3">
              <div className="font-heading font-bold">Estimate lines</div>
              <span className="ml-auto text-xs text-zinc-500 font-mono-accent">{lines.length}</span>
              <button onClick={() => addLine()} className="text-xs bg-zinc-900 text-white px-2 py-1 rounded" data-testid="add-custom-line-btn">
                <Plus className="w-3 h-3 inline mr-1" /> Custom line
              </button>
            </div>
            {lines.length === 0 && (
              <div className="text-sm text-zinc-500 text-center py-6">
                No lines yet. Pick services or measurements from the left to begin.
              </div>
            )}
            <div className="space-y-3">
              {lines.map((l, idx) => {
                const p = priced.lines[idx];
                const belowTarget = marginTarget > 0 && p && p.customer_price > 0 && p.margin_pct < marginTarget;
                return (
                  <LineRow
                    key={l._id}
                    line={l}
                    priced={p}
                    calculators={calculators}
                    belowTarget={belowTarget}
                    marginTarget={marginTarget}
                    onChange={(patch) => updateLine(idx, patch)}
                    onRemove={() => removeLine(idx)}
                    idx={idx}
                    savedMeasurements={measurements}
                    onOpenMeasureDialog={(i) => {
                      if (!selectedProp) { toast.error("Pick or create a property first."); return; }
                      setMeasureLineIdx(i);
                    }}
                    onPickSavedMeasurement={(i, m) => {
                      updateLine(i, {
                        measurement_id: m.id,
                        quantity: m.output_value,
                        calculator_key: m.calculator_key || lines[i]?.calculator_key,
                      });
                      toast.success(`Applied ${m.label || "measurement"} (${Number(m.output_value || 0).toLocaleString()} ${m.output_unit || m.input_unit})`);
                    }}
                    onClearMeasurement={(i) => updateLine(i, { measurement_id: null })}
                  />
                );
              })}
            </div>
          </div>

          <UpsellStrip
            suggestions={upsells}
            dismissed={dismissedUpsells}
            onAdd={addUpsellLine}
            onDismiss={(sid) => setDismissedUpsells((d) => new Set([...d, sid]))}
          />

          <div className="bg-white border border-zinc-200 rounded-xl p-4 card-crisp">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="estimator-title-input" />
              </div>
              <div>
                <Label>Internal notes (not printed on estimate)</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="estimator-notes-input" />
              </div>
            </div>
          </div>

          <SummaryCard summary={summary} belowTargetCount={belowTargetCount} marginTarget={marginTarget} />
        </div>
      </div>

      <NewPropertyDialog
        open={showNewProp}
        onClose={() => setShowNewProp(false)}
        customerId={customerId}
        customerAddress={customer.address}
        onCreated={(p) => { setProperties((xs) => [p, ...xs]); setSelectedProp(p.id); }}
      />
      <NewMeasurementDialog
        open={showNewMeas}
        onClose={() => setShowNewMeas(false)}
        propertyId={selectedProp}
        propertyAddress={properties.find(p => p.id === selectedProp)?.address}
        propertyLat={properties.find(p => p.id === selectedProp)?.lat}
        propertyLng={properties.find(p => p.id === selectedProp)?.lng}
        calculators={calculators}
        onCreated={(m) => setMeasurements((xs) => [m, ...xs])}
      />
      <LineMeasureDialog
        open={measureLineIdx !== null}
        onClose={() => setMeasureLineIdx(null)}
        propertyId={selectedProp}
        propertyAddress={properties.find(p => p.id === selectedProp)?.address}
        propertyLat={properties.find(p => p.id === selectedProp)?.lat}
        propertyLng={properties.find(p => p.id === selectedProp)?.lng}
        calculatorKey={measureLineIdx !== null ? lines[measureLineIdx]?.calculator_key : null}
        calculators={calculators}
        defaultLabel={measureLineIdx !== null ? (lines[measureLineIdx]?.service_name || "") : ""}
        existingMeasurement={
          measureLineIdx !== null && lines[measureLineIdx]?.measurement_id
            ? measurements.find((m) => m.id === lines[measureLineIdx].measurement_id)
            : null
        }
        onApply={({ measurement_id, output_value, measurement }) => {
          if (measureLineIdx === null) return;
          updateLine(measureLineIdx, {
            measurement_id,
            quantity: output_value,
          });
          setMeasurements((xs) => {
            const others = xs.filter((x) => x.id !== measurement.id);
            return [measurement, ...others];
          });
        }}
      />
      <AiDraftDialog
        open={showAi}
        onClose={() => setShowAi(false)}
        customerId={customerId}
        propertyId={selectedProp}
        onApprove={addAiLines}
      />
      <PropertyHistoryDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        propertyId={selectedProp}
      />
    </div>
  );
}
